// src/components/Chat/ClientCardDialog.tsx
import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import SellIcon from '@mui/icons-material/Sell';
import { ClientCard, fetchClientCard } from '../../api/clientCard';
import { fetchClient, IClientCard } from '../../api/clients';
import { apiErrorMessage } from '../../api/errors';
import { spendLesson, fetchSaleUsages, LessonUsageInfo } from '../../api/subscriptions';
import { channelLabel } from './clientChannels';
import { formatRubles } from './money';
import ClientEditDialog from './ClientEditDialog';
import SellSubscriptionDialog from './SellSubscriptionDialog';

interface ClientCardDialogProps {
    open: boolean;
    onClose: () => void;
    /** Организация консоли; `''` — организация ещё не выбрана. */
    mode: string;
    /** Телефон клиента, если он есть в объекте чата. */
    phone?: string | null;
    /** Id беседы: бэкенд умеет достать номер из беседы. */
    conversationId?: string | null;
    /** Имя из шапки диалога — показываем, пока карточка грузится. */
    clientName?: string | null;
    /**
     * Id клиента из справочника. Нужен, когда ни телефона, ни беседы нет:
     * агрегатный `/v1/clients/card` ищет клиента по номеру, а из поиска
     * приходит только запись справочника.
     */
    initialClientId?: string | null;
    /** Карточка изменилась (правка клиента, продажа, списание). */
    onChanged?: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
    telegram: 'Telegram',
    whatsapp: 'WhatsApp',
    avito: 'Авито',
    vk: 'ВКонтакте',
    max: 'MAX',
    orion: 'Орион',
    crm: 'CRM',
};

const LESSON_STATUS_LABELS: Record<string, string> = {
    scheduled: 'Запланирован',
    in_progress: 'Идёт сейчас',
    completed: 'Проведён',
};

const formatDateTime = (value?: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatDuration = (seconds?: number | null): string => {
    if (seconds === null || seconds === undefined) return '—';
    const safe = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(safe / 60);
    const rest = safe % 60;

    return minutes > 0 ? `${minutes} мин ${rest} с` : `${rest} с`;
};

const formatCount = (count: number, one: string, few: string, many: string): string => {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} ${one}`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} ${few}`;

    return `${count} ${many}`;
};

/** Дата списания: в карточке хватает дня, время только мешает читать список. */
const formatUsageDate = (value?: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

/**
 * История списаний занятий по одному абонементу.
 *
 * Пусто и ошибку показываем по-разному: «списаний не было» — это факт,
 * а упавший запрос молчим (история вспомогательная, диалог не ломаем).
 */
const SaleUsages: React.FC<{
    usages: LessonUsageInfo[];
    loading: boolean;
    failed: boolean;
}> = ({ usages, loading, failed }) => {
    if (loading) {
        return (
            <Typography variant="caption" color="text.secondary">
                Загружаем списания…
            </Typography>
        );
    }

    if (failed) return null;

    if (usages.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                Списаний пока не было
            </Typography>
        );
    }

    return (
        <Stack spacing={0.25}>
            {usages.map((usage) => (
                <Typography
                    key={usage.id}
                    variant="caption"
                    color={usage.isRevoked ? 'text.disabled' : 'text.secondary'}
                    sx={{ display: 'block' }}
                >
                    {formatUsageDate(usage.usedAt)} — списано
                    {usage.isRevoked
                        ? ` (возврат ${formatUsageDate(usage.revokedAt)}) · возвращено`
                        : ''}
                </Typography>
            ))}
        </Stack>
    );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ py: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
            {value}
        </Typography>
    </Box>
);

/**
 * Карточка из записи справочника — для случая «известен только id».
 *
 * Агрегатный `/v1/clients/card` ищет клиента по номеру телефона, поэтому
 * клиент из глобального поиска приходит сюда без телефона и без беседы.
 * Сводки берём из справочника, а списки звонков и занятий оставляем пустыми:
 * их в записи списка нет, а пустой список компонент уже умеет показывать.
 */
const cardFromDirectory = (client: IClientCard): ClientCard => ({
    phone: {
        input: client.phone ?? '',
        comparable: '',
        display: client.phone ?? '',
    },
    contact: null,
    client: {
        id: client.id,
        name: client.name,
        phone: client.phone,
        email: client.email,
        company: client.company,
        note: client.note,
        channel: client.channel ?? null,
    },
    chats: client.chats,
    calls: {
        total: client.calls.total,
        missed: client.calls.missed,
        lastCallAt: client.calls.lastCallAt,
        recent: [],
    },
    lessons: {
        bookedRecently: false,
        lastLessonAt: client.lessons.lastLessonAt,
        nextLessonAt: client.lessons.nextLessonAt,
        appointments: [],
    },
    organization: client.organization ?? null,
    manager: null,
});

export const ClientCardDialog: React.FC<ClientCardDialogProps> = ({
    open,
    onClose,
    mode,
    phone,
    conversationId,
    clientName,
    initialClientId,
    onChanged,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [card, setCard] = useState<ClientCard | null>(null);
    // Полная карточка из справочника: в ней есть абонементы и заметка.
    const [client, setClient] = useState<IClientCard | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [spendingId, setSpendingId] = useState<string | null>(null);
    // История списаний по абонементам: ключ — id продажи.
    const [usages, setUsages] = useState<Record<string, LessonUsageInfo[]>>({});
    const [usagesFailed, setUsagesFailed] = useState<Record<string, boolean>>({});
    const [usagesLoading, setUsagesLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [sellOpen, setSellOpen] = useState(false);

    const hasMode = Boolean(mode && mode.trim());
    const hasPhone = Boolean(phone && phone.trim());
    const hasConversation = Boolean(conversationId && conversationId.trim());
    // Запись справочника из поиска: телефона и беседы у неё может не быть.
    const directoryClientId = (initialClientId ?? '').trim();
    const hasInitialClient = directoryClientId !== '';

    const load = useCallback(async () => {
        // Без организации бэкенд карточку не отдаст — окно показывает подсказку.
        if (!hasMode) {
            setCard(null);
            setClient(null);
            setError(null);
            setLoading(false);
            return;
        }

        // Из поиска известен только id записи справочника: карточку по номеру
        // не собрать, зато остальные блоки есть в самой записи.
        if (!hasPhone && !hasConversation && hasInitialClient) {
            setLoading(true);
            setError(null);
            setCard(null);
            setClient(null);

            try {
                const data = await fetchClient(directoryClientId, mode);
                setClient(data);
                setCard(cardFromDirectory(data));
            } catch (e) {
                setCard(null);
                setClient(null);
                setError(apiErrorMessage(e, 'Не удалось загрузить карточку'));
            } finally {
                setLoading(false);
            }

            return;
        }

        if (!hasPhone && !hasConversation) {
            setCard(null);
            setClient(null);
            setError('Нет данных о клиенте');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        // Меняется клиент (phone/conversationId) — старые данные не показываем.
        setCard(null);
        setClient(null);

        try {
            const data = await fetchClientCard({ mode, phone, conversationId });
            setCard(data);

            const clientId = data.client?.id ?? data.contact?.id ?? null;
            if (clientId) {
                try {
                    setClient(await fetchClient(clientId, mode));
                } catch (e) {
                    // Агрегатная карточка уже загружена: показываем её без
                    // абонементов, но не ломаем окно из-за справочника.
                    setClient(null);
                }
            }
        } catch (e) {
            setCard(null);
            setClient(null);
            setError(apiErrorMessage(e, 'Не удалось загрузить карточку'));
        } finally {
            setLoading(false);
        }
    }, [hasMode, hasPhone, hasConversation, hasInitialClient, directoryClientId, mode, phone, conversationId]);

    const reload = useCallback(async () => {
        await load();
        onChanged?.();
    }, [load, onChanged]);

    useEffect(() => {
        if (!open) {
            setEditOpen(false);
            setSellOpen(false);
            setActionError(null);
            return;
        }

        void load();
    }, [open, load]);

    // Сброс состояния при закрытии: при следующем открытии (уже другого клиента)
    // не должно быть ни старых данных, ни старой ошибки.
    useEffect(() => {
        if (open) return;

        setCard(null);
        setClient(null);
        setError(null);
        setLoading(false);
    }, [open]);

    const directoryClient = card?.client ?? null;
    const clientId = client?.id ?? directoryClient?.id ?? card?.contact?.id ?? null;
    const displayName =
        client?.name || directoryClient?.name || card?.contact?.name || clientName || '';
    const displayPhone =
        client?.phone || directoryClient?.phone || card?.phone?.display || card?.phone?.input || phone || '';
    const telHref = displayPhone ? `tel:${displayPhone.replace(/[^\d+]/g, '')}` : '';
    const channel = client?.channel || directoryClient?.channel || '';
    const note = client?.note || directoryClient?.note || '';
    const clientEmail = client?.email || directoryClient?.email || '';
    const clientCompany = client?.company || directoryClient?.company || card?.contact?.company || '';
    const sales = client?.sales ?? [];
    const saleIdsKey = sales.map((sale) => sale.id).join(',');

    // История списаний: грузим на текущий набор абонементов, повторно — после
    // списания занятия. Ошибка по одному абонементу не мешает остальным
    // и не ломает диалог.
    const loadUsages = useCallback(async (): Promise<void> => {
        if (!hasMode || saleIdsKey === '') return;

        const saleIds = saleIdsKey.split(',');
        const loaded: Record<string, LessonUsageInfo[]> = {};
        const failed: Record<string, boolean> = {};

        setUsagesLoading(true);

        await Promise.all(
            saleIds.map(async (saleId) => {
                try {
                    loaded[saleId] = await fetchSaleUsages(mode, saleId);
                } catch (e) {
                    // История вспомогательная: упавший запрос просто не показываем.
                    failed[saleId] = true;
                    console.error('Не удалось загрузить списания по абонементу', saleId, e);
                }
            }),
        );

        setUsages(loaded);
        setUsagesFailed(failed);
        setUsagesLoading(false);
    }, [hasMode, mode, saleIdsKey]);

    useEffect(() => {
        if (!open || !hasMode || saleIdsKey === '') {
            setUsages({});
            setUsagesFailed({});
            setUsagesLoading(false);
            return;
        }

        void loadUsages();
    }, [open, hasMode, saleIdsKey, loadUsages]);

    const canEdit = Boolean(clientId);
    const canCreate = !clientId && Boolean(displayPhone || clientName?.trim());
    const canSell = Boolean(clientId) || Boolean(displayPhone && displayName);

    const isEmpty =
        card !== null &&
        card.client == null &&
        card.contact === null &&
        card.chats.conversationCount === 0 &&
        card.calls.total === 0 &&
        card.lessons.appointments.length === 0;

    const title = displayName || 'Карточка клиента';

    const handleSpend = async (saleId: string) => {
        if (!hasMode) return;

        setSpendingId(saleId);
        setActionError(null);

        try {
            await spendLesson(mode, saleId);
            await reload();
            // Списание добавилось в историю — перечитываем её по всем абонементам.
            await loadUsages();
        } catch (e) {
            setActionError(apiErrorMessage(e, 'Не удалось списать занятие'));
        } finally {
            setSpendingId(null);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
                    <PersonIcon color="action" />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {card?.phone?.display || phone || 'Карточка клиента'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Закрыть">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && !hasMode && (
                        <Alert severity="info">Выберите организацию</Alert>
                    )}

                    {!loading && hasMode && error && (
                        <Alert
                            severity={error === 'Нет данных о клиенте' ? 'info' : 'error'}
                            action={
                                hasPhone || hasConversation ? (
                                    <Button color="inherit" size="small" onClick={() => void load()}>
                                        Повторить
                                    </Button>
                                ) : undefined
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {!loading && hasMode && !error && isEmpty && (
                        <Alert severity="info">Нет данных о клиенте</Alert>
                    )}

                    {!loading && hasMode && !error && card && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1.5 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PhoneIcon />}
                                disabled={!telHref}
                                onClick={() => {
                                    if (telHref) window.location.href = telHref;
                                }}
                            >
                                Позвонить
                            </Button>
                            {canEdit && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setEditOpen(true)}
                                >
                                    Редактировать
                                </Button>
                            )}
                            {canCreate && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PersonAddIcon />}
                                    onClick={() => setEditOpen(true)}
                                >
                                    Создать клиента
                                </Button>
                            )}
                            {canSell && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SellIcon />}
                                    onClick={() => setSellOpen(true)}
                                >
                                    Продать абонемент
                                </Button>
                            )}
                        </Box>
                    )}

                    {!loading && hasMode && !error && card && !isEmpty && (
                        <Box>
                            <Section title="Контакт">
                                <Row label="Имя" value={displayName || '—'} />
                                <Row label="Телефон" value={displayPhone || '—'} />
                                <Row label="Компания" value={clientCompany || '—'} />
                                <Row label="Канал связи" value={channelLabel(channel) || '—'} />
                                {clientEmail ? <Row label="Email" value={clientEmail} /> : null}
                                <Row
                                    label="Источник"
                                    value={
                                        card.contact?.source
                                            ? SOURCE_LABELS[card.contact.source] || card.contact.source
                                            : '—'
                                    }
                                />
                                <Row
                                    label="Статус"
                                    value={
                                        card.contact?.isOnline
                                            ? 'в сети'
                                            : `был(а) ${formatDateTime(card.contact?.lastSeen)}`
                                    }
                                />
                                {note ? (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Заметка
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        >
                                            {note}
                                        </Typography>
                                    </Box>
                                ) : null}
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    {(card.contact?.tags || []).length > 0 ? (
                                        (card.contact?.tags || []).map((tag) => (
                                            <Chip key={tag} size="small" label={tag} />
                                        ))
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Теги не заполнены
                                        </Typography>
                                    )}
                                </Box>
                            </Section>

                            <Divider />

                            <Section title="Абонементы">
                                {!clientId && (
                                    <Typography variant="body2" color="text.secondary">
                                        Клиента ещё нет в справочнике — продайте абонемент, и он появится
                                        автоматически.
                                    </Typography>
                                )}
                                {clientId && sales.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        Абонементов нет
                                    </Typography>
                                )}
                                {sales.length > 0 && (
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                        {sales.map((sale) => (
                                            <Box
                                                key={sale.id}
                                                sx={{
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    p: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {sale.title || 'Абонемент'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatRubles(sale.totalAmount, sale.currency)}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block' }}
                                                >
                                                    Продажа: {formatDateTime(sale.soldAt)}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        Остаток:{' '}
                                                        {formatCount(
                                                            sale.remainingLessons,
                                                            'занятие',
                                                            'занятия',
                                                            'занятий',
                                                        )}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={
                                                            sale.remainingLessons <= 0 || spendingId !== null
                                                        }
                                                        onClick={() => void handleSpend(sale.id)}
                                                    >
                                                        {spendingId === sale.id
                                                            ? 'Списываем…'
                                                            : 'Списать занятие'}
                                                    </Button>
                                                </Box>
                                                <Box sx={{ mt: 0.75 }}>
                                                    <SaleUsages
                                                        usages={usages[sale.id] ?? []}
                                                        loading={usagesLoading}
                                                        failed={usagesFailed[sale.id] === true}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                                {actionError ? (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                        {actionError}
                                    </Alert>
                                ) : null}
                            </Section>

                            <Divider />

                            <Section title="Чаты">
                                <Row
                                    label="Беседы"
                                    value={formatCount(card.chats.conversationCount, 'беседа', 'беседы', 'бесед')}
                                />
                                <Row
                                    label="Сообщения"
                                    value={formatCount(
                                        card.chats.messageCount,
                                        'сообщение',
                                        'сообщения',
                                        'сообщений',
                                    )}
                                />
                                <Row label="Последнее сообщение" value={formatDateTime(card.chats.lastMessageAt)} />
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    {card.chats.channels.length > 0 ? (
                                        card.chats.channels.map((chatChannel) => (
                                            <Chip
                                                key={chatChannel}
                                                size="small"
                                                label={SOURCE_LABELS[chatChannel] || chatChannel}
                                                variant="outlined"
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Каналы не определены
                                        </Typography>
                                    )}
                                </Box>
                            </Section>

                            <Divider />

                            <Section title="Звонки">
                                <Row label="Всего звонков" value={card.calls.total} />
                                <Row label="Пропущено" value={card.calls.missed} />
                                <Row label="Последний звонок" value={formatDateTime(card.calls.lastCallAt)} />
                                {card.calls.recent.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Звонков не было
                                    </Typography>
                                ) : (
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                        {card.calls.recent.map((call) => (
                                            <Box
                                                key={call.id}
                                                sx={{
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    p: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {call.directionLabel || call.direction}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDateTime(call.startedAt)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {call.statusLabel || call.status}
                                                    {call.resultLabel ? ` • ${call.resultLabel}` : ''}
                                                    {` • ${formatDuration(call.durationSeconds)}`}
                                                    {call.managerName ? ` • ${call.managerName}` : ''}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Section>

                            <Divider />

                            <Section title="Записи на урок">
                                <Row
                                    label="Записывался недавно"
                                    value={card.lessons.bookedRecently ? 'да' : 'нет'}
                                />
                                <Row label="Последний урок" value={formatDateTime(card.lessons.lastLessonAt)} />
                                <Row label="Ближайший урок" value={formatDateTime(card.lessons.nextLessonAt)} />
                                {card.lessons.appointments.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Записей нет
                                    </Typography>
                                ) : (
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                        {card.lessons.appointments.map((lesson) => (
                                            <Box
                                                key={`${lesson.id}-${lesson.startAt}`}
                                                sx={{
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    p: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {lesson.title || 'Урок'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDateTime(lesson.startAt)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {LESSON_STATUS_LABELS[lesson.status] || lesson.status}
                                                    {lesson.employee ? ` • ${lesson.employee}` : ''}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Section>

                            {(card.organization || card.manager?.name) && (
                                <>
                                    <Divider />
                                    <Section title="Организация">
                                        <Row label="Название" value={card.organization?.name || '—'} />
                                        <Row label="Менеджер" value={card.manager?.name || '—'} />
                                        {card.manager?.number ? (
                                            <Row label="Телефон менеджера" value={card.manager.number} />
                                        ) : null}
                                    </Section>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <ClientEditDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                mode={mode}
                clientId={clientId}
                initialName={displayName}
                initialPhone={displayPhone}
                initialChannel={channel}
                initialNote={note}
                onSaved={() => {
                    setEditOpen(false);
                    void reload();
                }}
            />

            <SellSubscriptionDialog
                open={sellOpen}
                onClose={() => setSellOpen(false)}
                mode={mode}
                clientContactId={clientId}
                clientName={displayName}
                clientPhone={displayPhone}
                onSold={() => {
                    setSellOpen(false);
                    void reload();
                }}
            />
        </>
    );
};

export default ClientCardDialog;
